const CORRECTED_RECEIPT_STATUSES = new Set([
  'waiting_user',
  'completed',
  'partial',
]);

const FAILED_RECEIPT_COPY = Object.freeze({
  solicitar_contexto: Object.freeze({
    title: 'No se pudo completar la solicitud',
    summary: 'La solicitud de documentos no pudo completarse.',
  }),
  leer_fuente: Object.freeze({
    title: 'No se pudo consultar la fuente',
    summary: 'La consulta de la fuente no pudo completarse.',
  }),
  crear_editar_archivo: Object.freeze({
    title: 'No se pudo guardar el documento',
    summary: 'El documento no pudo guardarse.',
  }),
  revisar_documento: Object.freeze({
    title: 'No se pudo completar la revisión',
    summary: 'La revisión del documento no pudo completarse.',
  }),
  default: Object.freeze({
    title: 'No se pudo completar',
    summary: 'La acción no pudo completarse.',
  }),
});

const RETRYABLE_CONTINUATION_COPY = Object.freeze({
  title: 'La respuesta se interrumpió',
  summary: 'Tu mensaje y los documentos del chat se conservan. Al reintentar, se generará una nueva respuesta en esta conversación.',
});

const FAILED_RECEIPT_CODE_COPY = Object.freeze({
  document_too_large: Object.freeze({
    title: 'El documento supera el límite de edición',
    summary: 'La edición automática admite hasta 200.000 caracteres. Reformula tu solicitud para redactar solo la sección específica que necesitas y luego incorpórala o modifica el documento completo manualmente.',
  }),
  document_edit_recovery_exhausted: Object.freeze({
    title: 'No se guardaron cambios',
    summary: 'Se agotó la recuperación automática y no se guardó ninguna versión del documento.',
  }),
});
const USER_FACING_FAILURE_CODE = Symbol('user-facing-failure-code');

const receiptScopeKey = (receipt) => {
  if (!receipt || typeof receipt !== 'object') return '';
  const toolName = String(receipt.tool_name || '').trim();
  if (!toolName) return '';
  const turnId = String(receipt.turn_id || '').trim();
  if (turnId) return `turn:${turnId}|tool:${toolName}`;
  const triggerMessageId = String(receipt.trigger_message_id || '').trim();
  return triggerMessageId ? `message:${triggerMessageId}|tool:${toolName}` : '';
};

const isCorrectedReceipt = (receipt) => (
  receipt
  && CORRECTED_RECEIPT_STATUSES.has(String(receipt.status || ''))
);

const isFailedReceipt = receipt => !!(receipt && receipt.status === 'failed');

const isEditedAwayFailure = receipt => !!(
  isFailedReceipt(receipt)
  && receipt.trigger_message_exists === false
  && receipt.response_message_exists === false
  && (
    String(receipt.trigger_message_id || '').trim()
    || String(receipt.response_message_id || '').trim()
  )
);

const receiptOrder = (receipt, index) => {
  const sequence = Number(receipt && receipt.event_seq);
  return {
    index,
    sequence: Number.isFinite(sequence) && sequence > 0 ? sequence : null,
  };
};

const isLaterOrder = (candidate, reference) => {
  if (!candidate || !reference) return false;
  if (
    candidate.sequence !== null
    && reference.sequence !== null
    && candidate.sequence !== reference.sequence
  ) return candidate.sequence > reference.sequence;
  return candidate.index > reference.index;
};

const normalizedSet = (values) => new Set(
  (Array.isArray(values) ? values : [])
    .map(value => String(value || '').trim())
    .filter(Boolean),
);

const belongsToActiveReply = (receipt, activeReplies) => {
  if (!receipt || receipt.status !== 'failed') return false;
  const responseMessageIds = normalizedSet(activeReplies && activeReplies.responseMessageIds);
  const turnIds = normalizedSet(activeReplies && activeReplies.turnIds);
  const triggerMessageIds = normalizedSet(activeReplies && activeReplies.triggerMessageIds);
  const responseMessageId = String(receipt.response_message_id || '').trim();
  const turnId = String(receipt.turn_id || '').trim();
  const triggerMessageId = String(receipt.trigger_message_id || '').trim();
  return !!(
    (responseMessageId && responseMessageIds.has(responseMessageId))
    || (turnId && turnIds.has(turnId))
    || (triggerMessageId && triggerMessageIds.has(triggerMessageId))
  );
};

/**
 * Return the user-facing receipt projection without changing durable history.
 *
 * Failed tool attempts stay hidden while their assistant reply is active: the
 * model may still recover without any user action. After the reply ends, only
 * the latest unresolved failure for each turn/tool is eligible for display.
 * Any later waiting or successful result supersedes every earlier failure.
 */
export const collapseSupersededPreflightReceipts = (receipts, activeReplies = {}) => {
  const values = Array.isArray(receipts) ? receipts : [];
  const groups = new Map();

  values.forEach((receipt, index) => {
    const key = receiptScopeKey(receipt);
    if (!key) return;
    if (!groups.has(key)) {
      groups.set(key, {
        latestCorrectedOrder: null,
        latestFailureOrder: null,
      });
    }
    const group = groups.get(key);
    const order = receiptOrder(receipt, index);
    if (isCorrectedReceipt(receipt)) {
      if (!group.latestCorrectedOrder || isLaterOrder(order, group.latestCorrectedOrder)) {
        group.latestCorrectedOrder = order;
      }
    }
    if (isFailedReceipt(receipt)) {
      if (!group.latestFailureOrder || isLaterOrder(order, group.latestFailureOrder)) {
        group.latestFailureOrder = order;
      }
    }
  });

  return values.filter((receipt, index) => {
    if (!isFailedReceipt(receipt)) return true;
    // Message editing replaces the original turn but intentionally retains its
    // durable tool history. Do not relocate that now-unanchored failure to the
    // bottom of the regenerated conversation.
    if (isEditedAwayFailure(receipt)) return false;
    if (belongsToActiveReply(receipt, activeReplies)) return false;
    const group = groups.get(receiptScopeKey(receipt));
    if (!group) return true;
    const order = receiptOrder(receipt, index);
    if (isLaterOrder(group.latestCorrectedOrder, order)) return false;
    return group.latestFailureOrder && group.latestFailureOrder.index === index;
  });
};

/**
 * Return a render-only projection for a terminal failure.
 *
 * Durable error metadata remains available in session state for recovery and
 * diagnostics, but backend-provided error copy must never reach user-visible
 * receipt cards or source timelines. Keep the authorized continuation retry
 * available without exposing other backend actions or diagnostics.
 */
export const userFacingToolReceipt = (receipt) => {
  if (!isFailedReceipt(receipt)) return receipt;
  const rawError = receipt.error && typeof receipt.error === 'object'
    ? receipt.error
    : null;
  const failureCode = String(
    rawError && rawError.code || receipt[USER_FACING_FAILURE_CODE] || '',
  );
  const canRetryContinuation = receipt.result_ref?.type === 'continuation'
    && Boolean(receipt.result_ref.id)
    && rawError?.retryable === true
    && Array.isArray(receipt.ui_payload?.actions)
    && receipt.ui_payload.actions.includes('Reintentar respuesta');
  const copy = canRetryContinuation
    ? RETRYABLE_CONTINUATION_COPY
    : (FAILED_RECEIPT_CODE_COPY[failureCode]
      || FAILED_RECEIPT_COPY[String(receipt.tool_name || '')]
      || FAILED_RECEIPT_COPY.default);
  const projected = {
    ...receipt,
    ui_payload: {
      title: copy.title,
      summary: copy.summary,
      preview: '',
      warnings: [],
      counts: {},
      actions: canRetryContinuation ? ['Reintentar respuesta'] : [],
      requested_documents: [],
      requests: [],
    },
    error: rawError ? { retryable: rawError.retryable === true } : null,
  };
  if (Object.prototype.hasOwnProperty.call(FAILED_RECEIPT_CODE_COPY, failureCode)) {
    Object.defineProperty(projected, USER_FACING_FAILURE_CODE, {
      value: failureCode,
    });
  }
  return projected;
};
